import React, { useEffect, useState } from 'react';
import { CheckCircle, X } from 'lucide-react';
import { ProfilePicture } from './ProfilePicture.jsx';

/**
 * Slides down from top when partner completes one of your tasks.
 * Auto-dismisses after 5 seconds.
 */
export function CompletionNotification({ theme, partner, taskTitle, points, message, reward, rewardEmoji, onClose }) {
  const [show, setShow] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    // Slide in
    requestAnimationFrame(() => setShow(true));
    // Auto-close after 5 seconds
    const t = setTimeout(handleClose, 5000);
    return () => clearTimeout(t);
  }, []);

  function handleClose() {
    setClosing(true);
    setTimeout(onClose, 300);
  }

  return (
    <div style={{
      position: 'fixed',
      top: 'env(safe-area-inset-top, 12px)',
      left: 12,
      right: 12,
      zIndex: 500,
      pointerEvents: closing ? 'none' : 'auto',
      transform: show && !closing ? 'translateY(0)' : 'translateY(-120%)',
      opacity: show && !closing ? 1 : 0,
      transition: 'transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.25s ease-out',
    }}>
      <div style={{
        background: theme.bgElevated,
        border: `1px solid ${theme.coral}40`,
        borderLeft: `4px solid ${theme.coral}`,
        borderRadius: 12,
        padding: 14,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
        maxWidth: 480,
        margin: '0 auto',
      }}>
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <ProfilePicture user={partner} size={36} theme={theme} />
          <div style={{
            position: 'absolute',
            bottom: -2, right: -2,
            background: theme.coral,
            borderRadius: '50%',
            padding: 2,
            display: 'flex',
            border: `2px solid ${theme.bgElevated}`,
          }}>
            <CheckCircle size={10} color="white" fill={theme.coral} />
          </div>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: theme.text }}>
            {partner?.name} løste din opgave
          </div>
          <div style={{
            fontSize: 12, color: theme.textMuted,
            marginTop: 2,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            "{taskTitle}"
          </div>
          {points > 0 && (
            <div style={{
              fontSize: 11, color: theme.coral,
              fontWeight: 600, marginTop: 4,
            }}>
              +{points} kærlighedspoint
            </div>
          )}
          {message && (
            <div style={{
              fontSize: 12, color: theme.text,
              fontStyle: 'italic',
              padding: '6px 8px',
              background: theme.bgCard,
              borderRadius: 6,
              marginTop: 6,
            }}>
              💬 "{message}"
            </div>
          )}
          {reward && (
            <div style={{
              fontSize: 12, color: theme.gold,
              fontWeight: 600,
              padding: '6px 8px',
              background: `${theme.gold}15`,
              borderRadius: 6,
              marginTop: 6,
            }}>
              {rewardEmoji || '🎁'} {reward}
            </div>
          )}
        </div>

        <button onClick={handleClose} style={{
          background: 'transparent',
          border: 'none',
          color: theme.textMuted,
          cursor: 'pointer',
          padding: 2,
          flexShrink: 0,
        }}>
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
